import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiActivity, FiCalendar, FiUser, FiTruck } from 'react-icons/fi';
import Modal from './ui/Modal';
import Button from './ui/Button';

// ponytail: one chip recipe for every info row — the per-row colors carried no meaning
const CHIP_CLASS = 'shrink-0 w-8 h-8 bg-accent-soft rounded-lg flex items-center justify-center';
const CHIP_ICON_CLASS = 'text-accent';

const JobCardPreviewModal = ({ jobCard, onClose }) => {
    const { t } = useTranslation();
    if (!jobCard) return null;

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={t('accessRestricted')}
            description={t('notAssignedToYou')}
            size="md"
            footer={
                <Button variant="secondary" fullWidth onClick={onClose}>
                    {t('close')}
                </Button>
            }
        >
            {/* Warning banner */}
            <div className="flex items-center gap-3 p-4 mb-4 bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 rounded-lg">
                <div className="shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white" size={24} />
                </div>
                <p className="text-content font-medium">
                    {t('notAssignedToJobCard')}
                </p>
            </div>

            {/* Job Card Info */}
            <div className="space-y-4">
                <div className="bg-surface-2 rounded-lg p-4 border border-edge">
                    <h4 className="text-sm font-semibold text-content-muted mb-3">{t('jobCardInformation')}</h4>

                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className={CHIP_CLASS}>
                                <FiCalendar className={CHIP_ICON_CLASS} size={16} />
                            </div>
                            <div>
                                <p className="text-xs text-content-muted">{t('jobNumber')}</p>
                                <p dir="ltr" className="text-sm font-semibold text-content tabular-nums">{jobCard.job_number}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className={CHIP_CLASS}>
                                <FiUser className={CHIP_ICON_CLASS} size={16} />
                            </div>
                            <div>
                                <p className="text-xs text-content-muted">{t('customer')}</p>
                                <p className="text-sm font-semibold text-content">
                                    {jobCard.customer?.first_name} {jobCard.customer?.last_name}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className={CHIP_CLASS}>
                                <FiTruck className={CHIP_ICON_CLASS} size={16} />
                            </div>
                            <div>
                                <p className="text-xs text-content-muted">{t('vehicle')}</p>
                                <p className="text-sm font-semibold text-content">
                                    {jobCard.vehicle?.make} {jobCard.vehicle?.model} ({jobCard.vehicle?.year})
                                </p>
                                <p dir="ltr" className="text-xs text-content-muted tabular-nums">{jobCard.vehicle?.plate_number}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className={CHIP_CLASS}>
                                <FiActivity className={CHIP_ICON_CLASS} size={16} />
                            </div>
                            <div>
                                <p className="text-xs text-content-muted">{t('status')}</p>
                                <p className="text-sm font-semibold text-content capitalize">
                                    {jobCard.status ? t(`status.${jobCard.status}`, { defaultValue: jobCard.status.replace(/_/g, ' ') }) : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300 text-center">
                        <strong>{t('noteLabel')}:</strong> {t('previewSupervisorNote')}
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default JobCardPreviewModal;
